// userAnalytics.tsx
import { useEffect } from "react";

type AnalyticsEvent = {
    eventId: string;
    timestamp: string;
    path: string;
    action_name: string;
    properties: Record<string, unknown>;
};

type AnalyticsConfig = {
    endpoint: string;
    userId?: string;
    intervalMs?: number;
    getHeaders: () => Promise<Record<string, string> | null>;
};

const SESSION_KEY = "analytics_session_id";

let sessionId: string | undefined;
let config: AnalyticsConfig | undefined;
let queue: AnalyticsEvent[] = [];
let sending = false;

function getSessionId(): string {
    if (sessionId) return sessionId;

    try {
        sessionId = sessionStorage.getItem(SESSION_KEY) || crypto.randomUUID();
        sessionStorage.setItem(SESSION_KEY, sessionId);
    } catch {
        sessionId = crypto.randomUUID();
    }

    return sessionId;
}

export function trackEvent(
    action_name: string,
    properties: Record<string, unknown> = {}
): void {
    if (typeof window === "undefined") return;

    queue.push({
        eventId: crypto.randomUUID(),
        timestamp: new Date().toISOString(),
        path: window.location.pathname,
        action_name,
        properties,
    });
    console.log(queue);
}

export function trackPageView(path = window.location.pathname): void {
    trackEvent("page_view", { path });
}

export async function flushAnalytics(): Promise<void> {
    const currentConfig = config;

    if (!currentConfig || sending || queue.length === 0) return;

    sending = true;

    // New events can continue entering the queue during this request.
    const batch = queue.slice(0, 100);

    try {
        const headers = await currentConfig.getHeaders();

        if (!headers) return;

        const response = await fetch(currentConfig.endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...headers,
                "Request-Platform": "web",
                "X-Request-Type": "save_user_analytics",
            },
            body: JSON.stringify({
                sessionId: getSessionId(),
                userId: currentConfig.userId ?? null,
                events: batch,
            }),
        });

        if (!response.ok) {
            throw new Error(
                `Analytics request failed with status: ${response.status}`
            );
        }

        // Backend must return success only after saving the entire batch.
        queue.splice(0, batch.length);
    } catch (error) {
        console.error("Failed to send analytics batch:", error);
        // Keep the events queued for retry.
    } finally {
        sending = false;
    }
}

// posts analytics every 15sec or on page hide/visibility change
export function useUserAnalytics({
    endpoint,
    userId,
    intervalMs = 15_000,
    getHeaders,
}: AnalyticsConfig): void {
    useEffect(() => {
        config = { endpoint, userId, intervalMs, getHeaders };

        const timer = window.setInterval(() => {
            void flushAnalytics();
        }, intervalMs);

        const handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                void flushAnalytics();
            }
        };

        const handlePageHide = () => {
            void flushAnalytics();
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("pagehide", handlePageHide);

        return () => {
            window.clearInterval(timer);
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
            window.removeEventListener("pagehide", handlePageHide);

            void flushAnalytics();
        };
    }, [endpoint, userId, intervalMs, getHeaders]);
}